import React, { useEffect, useMemo } from "react";
import { Modal, Form, Input, InputNumber, Select, Button, Space, Typography, Upload } from "antd";

const { Text } = Typography;

const normalizeOptions = (options) => {
    if (!Array.isArray(options)) return [];
    return options.map((o) => {
        if (o && typeof o === "object") {
            return {
                label: o.label ?? String(o.value ?? ""),
                value: o.value,
                disabled: !!o.disabled,
            };
        }
        return { label: String(o), value: o };
    });
};

const safeNumber = (v) => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const EditModal = ({
                       open,
                       title,
                       onClose,
                       onSubmit,
                       fields = [],
                       initialValues = {},
                       confirmLoading = false,
                       okText = "Сохранить",
                       cancelText = "Отмена",
                       width = 520,
                       destroyOnClose = true,
                       maskClosable = false,
                   }) => {
    const [form] = Form.useForm();

    const preparedFields = useMemo(() => {
        return (fields || []).map((f) => ({
            type: "input",
            ...f,
        }));
    }, [fields]);

    useEffect(() => {
        if (open) {
            form.setFieldsValue(initialValues || {});
        } else {
            form.resetFields();
        }
    }, [open, form, initialValues]);

    const handleFinish = async (values) => {
        await onSubmit?.(values, form);
    };

    const normFile = (e) => {
        if (Array.isArray(e)) return e;
        return e?.fileList;
    };

    const renderField = (field) => {
        const {
            name,
            label,
            type,
            placeholder,
            disabled,
            rules,
            options,
            mode,
            props,
            quickValues,
            min,
            max,
            step,
            precision,
            showReset,
        } = field;

        if (!name && type !== "text") return null;

        if (type === "text") {
            return (
                <Form.Item key={String(name || label || Math.random())} label={label}>
                    {name ? (
                        <Form.Item noStyle shouldUpdate>
                            {() => <Text>{String(form.getFieldValue(name) ?? "")}</Text>}
                        </Form.Item>
                    ) : (
                        <Text>{String(field.value ?? "")}</Text>
                    )}
                </Form.Item>
            );
        }

        if (type === "select") {
            return (
                <Form.Item key={String(name)} name={name} label={label} rules={rules}>
                    <Select placeholder={placeholder} disabled={disabled} options={normalizeOptions(options)} mode={mode} {...props} />
                </Form.Item>
            );
        }

        if (type === "number") {
            return (
                <Form.Item key={String(name)} name={name} label={label} rules={rules}>
                    <InputNumber
                        style={{ width: "100%" }}
                        placeholder={placeholder}
                        disabled={disabled}
                        min={min}
                        max={max}
                        step={step}
                        precision={precision}
                        {...props}
                    />
                </Form.Item>
            );
        }

        if (type === "upload") {
            const uploadProps = {
                beforeUpload: () => false,
                maxCount: 1,
                ...props,
            };

            return (
                <Form.Item key={String(name)} name={name} label={label} valuePropName="fileList" getValueFromEvent={normFile} rules={rules}>
                    <Upload disabled={disabled} {...uploadProps}>
                        <Button disabled={disabled}>{placeholder || "Выбрать файл"}</Button>
                    </Upload>
                </Form.Item>
            );
        }

        if (type === "count") {
            const qv = Array.isArray(quickValues) ? quickValues : [];
            const minValue = typeof min === "number" ? min : 0;

            const mergedRules = [
                ...(rules || []),
                {
                    validator: async (_, value) => {
                        if (value === undefined || value === null || value === "") return;
                        const n = Number(value);
                        if (!Number.isFinite(n)) throw new Error("Введите число");
                        if (n < minValue) throw new Error(`Значение не может быть меньше ${minValue}`);
                    },
                },
            ];

            return (
                <Form.Item key={String(name)} label={label}>
                    <Space direction="vertical" style={{ width: "100%" }} size={8}>
                        <Form.Item name={name} noStyle rules={mergedRules}>
                            <InputNumber
                                style={{ width: "100%" }}
                                placeholder={placeholder}
                                disabled={disabled}
                                min={minValue}
                                max={max}
                                step={step}
                                precision={precision}
                                {...props}
                            />
                        </Form.Item>
                        {qv.length > 0 && (
                            <Space wrap>
                                {qv.map((v) => {
                                    const addValue = safeNumber(v);
                                    const btnText = addValue >= 0 ? `+${addValue}` : String(addValue);
                                    return (
                                        <Button
                                            key={String(v)}
                                            disabled={disabled}
                                            onClick={() => {
                                                const current = safeNumber(form.getFieldValue(name));
                                                const next = Math.max(minValue, current + addValue);
                                                form.setFieldsValue({ [name]: next });
                                            }}
                                        >
                                            {btnText}
                                        </Button>
                                    );
                                })}
                                {showReset !== false && (
                                    <Button
                                        disabled={disabled}
                                        onClick={() => {
                                            form.setFieldsValue({ [name]: minValue });
                                        }}
                                    >
                                        Сбросить
                                    </Button>
                                )}
                            </Space>
                        )}
                    </Space>
                </Form.Item>
            );
        }

        return (
            <Form.Item key={String(name)} name={name} label={label} rules={rules}>
                <Input placeholder={placeholder} disabled={disabled} {...props} />
            </Form.Item>
        );
    };

    return (
        <Modal
            open={open}
            title={title}
            onCancel={onClose}
            onOk={() => form.submit()}
            okText={okText}
            cancelText={cancelText}
            confirmLoading={confirmLoading}
            width={width}
            destroyOnClose={destroyOnClose}
            maskClosable={maskClosable}
        >
            <Form form={form} layout="vertical" onFinish={handleFinish} initialValues={initialValues}>
                {preparedFields.map(renderField)}
            </Form>
        </Modal>
    );
};

export default EditModal;